/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, Fragment, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Flex, Button } from "@duxton-web/ui"
import notification from "components/common/notification"
import { find, replace, uniq } from "lodash"
import { ItemAvailabilityModal } from "components/modals/ItemAvailabilityModal"
import { BottomDrawer } from "components/common"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { actionCreator } from "store/store"
import { reportButtonTriggeredRumAction } from "store/middlewares/fetchLifecycleRum"
import { AppState } from "store/store.type"
import {
  MarkStatusesText,
  isRecurringItem,
  availableStatusesValue,
} from "utils/item_helper"
import {
  TroyWebTracker,
  getResponseErrorEventLabels,
  getResponseErrorEventLabelsForRTK,
} from "utils/tracking"
import {
  EventAction,
  EventActionEng,
  EventCategory,
  EventCategoryEng,
  EventEntryPoint,
} from "constants/tracking"
import { ConfirmModal } from "components/common/ConfirmModal"
import useInventoryContext from "hooks/useInventoryContext"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import {
  useDeleteItemMutation,
  useUpdateItemsAvailableV1Mutation,
} from "queries"
import { updateItemsAvailableStatus } from "utils/rtk_migration_helper"

export interface MultiItemSelectionDrawerProps {
  visible: boolean
  onClose(e: any): void
}

export const MultiItemSelectionDrawer: FC<MultiItemSelectionDrawerProps> = ({
  visible,
  onClose,
}: MultiItemSelectionDrawerProps) => {
  const [showStatusModal, toggleStatusModal] = useState(false)
  const [showDeleteModal, toggleDeleteModal] = useState(false)
  const dispatch = useDispatch()
  const { isEditAllowed, isStatusChangeAllowed } = useMenuEditorStatus()
  const enablePartialMenu = useEnablePartialMenu()

  const {
    flags: {
      deleteItem: isRtkDeleteItemEnabled,
      updateItemsAvailableStatus: isRtkUpdateItemsAvailableStatusEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [deleteItemRtk] = useDeleteItemMutation()
  const [updateItemsAvailableV1] = useUpdateItemsAvailableV1Mutation()

  const {
    merchantID,
    drawer: { itemSelections },
    items,
    isMartDepartmentStructure,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    overview: { sellingTime },
  } = useSelector((state: AppState) => state.menu)

  const { disableAvailability } = useInventoryContext()

  // @ts-ignore
  const count = Object.values(itemSelections ?? {}).reduce(
    (acc: number, selected: boolean) => acc + (selected ? 1 : 0),
    0,
  )

  const itemIDs = Object.keys(itemSelections ?? {}).filter(
    itemID => itemSelections?.[itemID] === true,
  )

  const categoryIDs = uniq(
    items
      .filter(({ itemID }) => itemIDs.includes(itemID ?? ""))
      .map(({ categoryID }) => categoryID),
  )

  const departmentIDs = uniq(
    items
      .filter(({ itemID }) => itemIDs.includes(itemID ?? ""))
      .map(({ departmentID }) => departmentID),
  )

  const subDepartmentIDs = uniq(
    items
      .filter(({ itemID }) => itemIDs.includes(itemID ?? ""))
      .map(({ subDepartmentID }) => subDepartmentID),
  )

  const getCategoryLabels = () =>
    isMartDepartmentStructure
      ? {
          CATEGORY_ID: departmentIDs,
          SUB_CATEGORY_ID: subDepartmentIDs,
        }
      : { CATEGORY_ID: categoryIDs }

  const getCategoryCountLabels = () =>
    isMartDepartmentStructure
      ? {
          NUM_CATEGORY_SELECTED: departmentIDs.length,
          NUM_SUB_CATEGORY_SELECTED: subDepartmentIDs.length,
        }
      : {
          NUM_CATEGORY_SELECTED: categoryIDs.length,
        }

  const markStatusesTextPlural = MarkStatusesText(true)
  const markStatusesTextSingular = MarkStatusesText(false)

  const textMap = useTranslationsMap({
    deleteTitle: "menu.modals.deleteItems.title",
    deleteContent: "menu.modals.deleteItems.content",
    deleteButton: "menu.buttons.delete",
    cancelButton: "menu.buttons.cancel",
    editSellingTimeButton: "menu.sellingTime.button.editSellingTime",
    deleteItemSuccess: "menu.notifications.deleteItem.successMessage",
    deleteItemsSuccess: "menu.notifications.deleteItems.successMessage",
    changeAvailability: "menu.buttons.changeAvailability",
  })

  const handleChangeAvailability = async (availableStatus: number) => {
    const successMessage =
      itemIDs.length > 1
        ? replace(
            markStatusesTextPlural[availableStatus],
            "<x>",
            `${itemIDs.length}`,
          )
        : markStatusesTextSingular[availableStatus]

    const result = await updateItemsAvailableStatus({
      dispatch,
      merchantID,
      merchantGroupID,
      menuGroupID,
      isRtkFlagLoading,
      isRtkUpdateItemsAvailableStatusEnabled,
      updateItemsAvailableV1Rtk: updateItemsAvailableV1,
      itemIDs,
      availableStatus,
      successMessage,
      isMartDepartmentStructure,
      isSellingTimeStructure,
      enablePartialMenu,
    })

    let labels = {
      ...(merchantID
        ? { STORE_ID: merchantID }
        : {
            MERCHANT_GROUP_ID: merchantGroupID,
            MENU_GROUP_ID: menuGroupID,
          }),
      ENTRY_POINT: EventEntryPoint.menuOverviewBottomNav,
      ITEM_ID: itemIDs,
      NO_ITEM_SELECTED: itemIDs.length,
      VALUE: availableStatusesValue[availableStatus],
      STATUS: result.succeeded ? "SUCCESS" : "FAILURE",
      REASON: "",
      ERROR_MSG: "",
      ...getCategoryLabels(),
      ...(enablePartialMenu && { IS_PARTIAL_MENU: true }),
    }
    if (!result.succeeded) {
      if (result.rtkError) {
        labels = {
          ...labels,
          ...getResponseErrorEventLabelsForRTK({ error: result.rtkError }),
        }
      } else if (result.legacyResp) {
        labels = {
          ...labels,
          ...getResponseErrorEventLabels(result.legacyResp),
        }
      }
    }
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.submitItemAvailability,
      labels,
    })
  }

  const handleClickChangeAvailability = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickChangeAvailability,
      labels: {
        ITEM_ID: itemIDs,
        NUM_ITEM_SELECTED: itemIDs.length,
        PAGELAYOUT: "BOTTOMNAV",
        ...getCategoryCountLabels(),
        ...(enablePartialMenu && { IS_PARTIAL_MENU: true }),
      },
    })
    toggleStatusModal(true)
  }

  const handleDelete = async () => {
    reportButtonTriggeredRumAction({
      trigger: "menu_delete_items",
      endpoint_name: "deleteItem",
    })
    dispatch(actionCreator.global.showLoading())
    let countSuccess = 0
    let lastError: any

    if (!isRtkFlagLoading && isRtkDeleteItemEnabled) {
      // RTK Query path
      for (let i = 0; i < itemIDs.length; i += 1) {
        const itemID = itemIDs[i]
        // eslint-disable-next-line no-await-in-loop
        const result = await deleteItemRtk({
          merchantID,
          merchantGroupID,
          itemID,
          menuGroupID,
        })

        if (result.error) {
          lastError = result
        } else {
          countSuccess += 1
        }
      }

      if (countSuccess > 0) {
        // Replicate closeDrawers middleware — closes all drawers and resets bulk selection
        dispatch(actionCreator.bulkSelectionsV3.reset())
        dispatch(
          actionCreator.menu.toggleDrawer({
            showSection: false,
            showCategory: false,
            showSubDepartment: false,
            showItem: false,
            showSortCategories: false,
            showSortItems: false,
            showSortDepartments: false,
            showSortSubDepartments: false,
            showSellingTimeEditor: false,
            showMultiSelectSellingTime: false,
            showItemSelection: false,
            itemSelections: {},
          }),
        )

        // Replicate refreshMenu middleware for non-partial-menu cases
        // (partial menu case is already handled by invalidatesTags on the endpoint)
        if (!isMartDepartmentStructure) {
          dispatch(
            actionCreator.menu.fetchMenu({
              merchantID,
              isRefreshing: true,
              isSellingTimeStructure,
              merchantGroupID,
              menuGroupID,
            }),
          )
        } else if (!enablePartialMenu) {
          dispatch(
            actionCreator.menu.fetchCatalogue({
              merchantID,
              isRefreshing: true,
              isSellingTimeStructure,
              merchantGroupID,
              menuGroupID,
            }),
          )
        }
      }

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.deleteItem,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkDeleteItemEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "deleteItem",
        },
      })
    } else if (!isRtkFlagLoading) {
      // Old Redux path
      let resp: any
      let itemID

      for (let i = 0; i < itemIDs.length; i += 1) {
        itemID = itemIDs[i]
        // eslint-disable-next-line no-await-in-loop
        resp = await dispatch(
          actionCreator.menu.deleteItem({
            merchantID,
            itemID,
            showConfirm: false,
            showSuccess: false,
            showGlobalLoading: false,
            merchantGroupID,
            menuGroupID,
          }),
        )
        if (resp?.type.endsWith("_SUCCESS")) {
          countSuccess += 1
        } else {
          lastError = resp
        }
      }

      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.deleteItem,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkDeleteItemEnabled,
          FROM_RTK_QUERY: false,
          FROM_OLD_REDUX: true,
          ACTION_NAME: "deleteItem",
        },
      })
    }

    let labels = {
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
      ENTRY_POINT: EventEntryPoint.menuOverviewBottomNav,
      ITEM_ID: itemIDs,
      NO_ITEM_SELECTED: itemIDs.length,
      STATUS: "SUCCESS",
      REASON: "",
      ERROR_MSG: "",
      ...(enablePartialMenu && { IS_PARTIAL_MENU: true }),
    }

    if (countSuccess !== itemIDs.length) {
      labels = {
        ...labels,
        STATUS: "FAILURE",
        ...(isRtkDeleteItemEnabled
          ? getResponseErrorEventLabelsForRTK(lastError)
          : getResponseErrorEventLabels(lastError)),
      }
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.submitDeleteItem,
      labels,
    })

    dispatch(
      actionCreator.menu.toggleDrawer({
        itemSelections: {},
      }),
    )

    dispatch(actionCreator.global.hideLoading())

    toggleDeleteModal(false)

    const message =
      countSuccess === 1
        ? textMap.deleteItemSuccess
        : replace(textMap.deleteItemsSuccess, "<x>", `${countSuccess}`)

    notification.success({
      message,
    })

    // Update Redux to remove deleted items from search results
    if (countSuccess > 0) {
      dispatch(
        actionCreator.menu.syncSearchItems(
          undefined,
          itemIDs.slice(0, countSuccess),
        ),
      )
    }
  }

  const confirmDelete = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickDeleteItem,
      labels: {
        ITEM_ID: itemIDs,
        NUM_ITEM_SELECTED: itemIDs.length,
        PAGELAYOUT: "BOTTOMNAV",
        ...getCategoryCountLabels(),
        ...(enablePartialMenu && { IS_PARTIAL_MENU: true }),
      },
    })
    toggleDeleteModal(true)
  }

  let hasRecurring = false
  let hasNonRecurring = false
  if (enablePartialMenu) {
    // When partial menu is active, items are paginated and not fully loaded
    // into Redux — match V3 drawer behaviour and treat all items as non-recurring.
    hasNonRecurring = true
  } else {
    Object.keys(itemSelections ?? {})?.forEach(ID => {
      if (itemSelections?.[ID] === true) {
        const item = find(items, ({ itemID }) => ID === itemID)
        if (item) {
          hasRecurring = hasRecurring || isRecurringItem(item)
          hasNonRecurring = hasNonRecurring || !isRecurringItem(item)
        }
      }
    })
  }

  return (
    <Fragment>
      <BottomDrawer
        data-testid="bottom-drawer"
        visible={visible}
        onClose={e => {
          TroyWebTracker.sendEvent({
            category: EventCategory.menuOverviewTab,
            action: EventAction.clickExitItemAction,
            labels: {
              ITEM_ID: itemIDs,
              NUM_ITEM_SELECTED: itemIDs.length,
              PAGELAYOUT: "BOTTOMNAV",
              ...getCategoryLabels(),
              ...getCategoryCountLabels(),
              ...(enablePartialMenu && { IS_PARTIAL_MENU: true }),
            },
          })
          onClose(e)
        }}
      >
        <ConfirmModal
          data-testid="delete-confirm-modal"
          title={replace(textMap.deleteTitle, "<x>", `${itemIDs.length}`)}
          content={textMap.deleteContent}
          visible={showDeleteModal}
          onCancel={() => toggleDeleteModal(false)}
          onConfirm={handleDelete}
        />
        <Flex align="center" gap={16}>
          <div style={{ fontWeight: 500, width: 200 }}>
            {useTranslation(
              count === 1
                ? "menu.overview.itemSelected"
                : "menu.overview.itemsSelected",
              { "<x>": `${count}` },
            )}
          </div>

          {isSellingTimeStructure && (
            <Button
              data-testid="change-selling-time-button"
              size="small"
              onClick={() => {
                dispatch(
                  actionCreator.menu.toggleDrawer({
                    showMultiSelectSellingTime: true,
                  }),
                )

                TroyWebTracker.sendEvent({
                  category: EventCategory.menuOverviewTab,
                  action: EventAction.setMultiSelectSellingTime,
                  labels: {
                    ...(merchantID
                      ? { STORE_ID: merchantID }
                      : {
                          MERCHANT_GROUP_ID: merchantGroupID,
                          MENU_GROUP_ID: menuGroupID,
                        }),
                    CATEGORY_ID: categoryIDs,
                    NUM_CATEGORY_SELECTED: categoryIDs.length,
                    ITEM_ID: itemIDs,
                    NUM_ITEM_SELECTED: itemIDs.length,
                    PAGELAYOUT: "BOTTOMNAV",
                    SELLING_TIME_FILTER: sellingTime,
                  },
                })
              }}
              disabled={!isEditAllowed || count === 0}
            >
              {textMap.editSellingTimeButton}
            </Button>
          )}

          {!disableAvailability && (
            <Button
              data-testid="change-availability-button"
              size="small"
              onClick={handleClickChangeAvailability}
              disabled={
                !isStatusChangeAllowed || count === 0 || isRtkFlagLoading
              }
            >
              {textMap.changeAvailability}
            </Button>
          )}

          <Button
            data-testid="delete-button"
            danger
            size="small"
            onClick={confirmDelete}
            disabled={!isEditAllowed || count === 0}
          >
            {useTranslation("menu.buttons.delete")}
          </Button>
        </Flex>
      </BottomDrawer>
      <ItemAvailabilityModal
        data-testid="item-availability-modal"
        visible={showStatusModal}
        hasRecurring={hasRecurring}
        hasNonRecurring={hasNonRecurring}
        itemCount={count}
        onClose={() => toggleStatusModal(false)}
        onConfirm={handleChangeAvailability}
      />
    </Fragment>
  )
}
